import React, { useState } from "react";
import { ArrowRight, Phone, Mail, MapPin, Clock, Send, Truck, GraduationCap } from "lucide-react";
import { BUSINESS_TYPES, VOLUMES, CONTACT } from "../../mock/mockData";
import { useToast } from "../../hooks/use-toast";

const initialForm = {
  name: "",
  company: "",
  email: "",
  phone: "",
  city: "",
  business: BUSINESS_TYPES[0],
  volume: VOLUMES[0],
  message: "",
};

const ApplyForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm((state) => ({ ...state, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast({ title: "Faltam campos", description: "Preencha nome, email e telemovel." });
      return;
    }

    setSubmitting(true);
    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
      const response = await fetch(`${baseUrl}/api/applications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (!response.ok) {
        throw new Error("application-submit-failed");
      }

      setSubmitted(true);
      toast({ title: "Candidatura enviada", description: "Respondemos em 48h uteis com tabela detalhada." });
    } catch (error) {
      toast({
        title: "Nao foi possivel enviar",
        description: "Tente novamente ou fale connosco pelo WhatsApp.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setForm(initialForm);
  };

  return (
    <section id="candidatura" className="border-t-[3px] border-black bg-[#E63946] text-white relative overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-24">
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-5">
            <span className="mono text-[11px] uppercase tracking-widest px-3 py-1 brut-border bg-white text-black">09</span>
            <span className="mono text-[11px] uppercase tracking-widest text-white/80">Proximo passo</span>
            <div className="flex-1 h-[3px] bg-white" />
          </div>
          <h2 className="display font-bold text-[36px] sm:text-[56px] md:text-[80px] lg:text-[112px] leading-[0.9]">
            Quer fazer parte
            <br />
            da <span className="italic font-normal underline decoration-[#FFD60A] decoration-[6px] underline-offset-[8px]">rede Veentto</span>?
          </h2>
          <p className="mt-6 max-w-[720px] text-[16px] md:text-[19px] leading-[1.5]">
            Preencha em 2 minutos. Respondemos em 48 horas com tabela detalhada e proposta de exclusividade para a sua zona.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-10">
          <form onSubmit={onSubmit} className="brut-border-thick bg-[#F4F0E8] text-black p-6 md:p-8">
            {submitted ? (
              <div className="py-10 text-center">
                <div className="display text-[40px] md:text-[56px] font-bold leading-none">Obrigado!</div>
                <p className="mono text-[12px] uppercase tracking-widest mt-4">A sua candidatura foi recebida.</p>
                <p className="text-[15px] mt-4">Respondemos em 48h uteis com tabela detalhada e proposta de exclusividade.</p>
                <button
                  type="button"
                  onClick={resetForm}
                  className="mt-8 inline-flex items-center gap-2 bg-black text-[#FFD60A] font-bold mono text-[12px] uppercase tracking-wider px-5 py-3 brut-border brut-btn"
                >
                  Nova candidatura
                </button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Nome completo" value={form.name} onChange={update("name")} required />
                <Field label="Empresa" value={form.company} onChange={update("company")} />
                <Field label="Email" type="email" value={form.email} onChange={update("email")} required />
                <Field label="Telemovel" value={form.phone} onChange={update("phone")} required />
                <Field label="Localidade" value={form.city} onChange={update("city")} />
                <Select label="Tipo de negocio" value={form.business} onChange={update("business")} options={BUSINESS_TYPES} />
                <Select label="Volume estimado (un./mes)" value={form.volume} onChange={update("volume")} options={VOLUMES} fullWidth />
                <div className="sm:col-span-2">
                  <Label>Mensagem (opcional)</Label>
                  <textarea
                    value={form.message}
                    onChange={update("message")}
                    rows={4}
                    placeholder="Conte-nos sobre a sua zona, clientes e objectivos."
                    className="w-full mt-2 brut-border bg-white px-4 py-3 text-[14px] outline-none focus:bg-[#FFD60A]/30 transition-colors"
                  />
                </div>
                <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4 mt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center justify-center gap-3 bg-black text-[#FFD60A] font-bold mono text-[14px] uppercase tracking-wider px-7 py-5 brut-border brut-btn disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {submitting ? "A enviar..." : "Enviar candidatura"}
                    <Send size={18} />
                  </button>
                  <span className="mono text-[11px] uppercase tracking-widest text-black/60">
                    Resposta em 48h uteis
                  </span>
                </div>
              </div>
            )}
          </form>

          <aside className="space-y-4">
            <div className="brut-border bg-black text-[#FFD60A] p-6">
              <div className="mono text-[10px] uppercase tracking-widest text-white/60 mb-1">Falar com a Veentto</div>
              <a href="tel:+351928272818" className="display text-[28px] md:text-[32px] font-bold leading-none flex items-center gap-3">
                <Phone size={22} />{CONTACT.phone}
              </a>
            </div>
            <ContactCard icon={<Mail size={18} />} label="Email comercial" value={CONTACT.email} link={`mailto:${CONTACT.email}`} />
            <ContactCard icon={<MapPin size={18} />} label="Showroom / Test drive" value={CONTACT.location} />
            <ContactCard icon={<MapPin size={18} />} label="Loja Lisboa" value="www.veentto.com" link={CONTACT.lisbonStoreUrl} external />
            <ContactCard icon={<Clock size={18} />} label="Horario" value={CONTACT.hours} />
            <div className="brut-border bg-white text-black p-5">
              <div className="flex items-start gap-3">
                <Truck size={19} className="mt-1 flex-shrink-0" />
                <div>
                  <div className="mono text-[10px] uppercase tracking-widest font-bold">Entrega</div>
                  <p className="mt-2 text-[13px] leading-[1.45]">
                    Valor calculado conforme volume e distância. O envio na caixa ou montado altera a logística; produto montado exige mais transporte e pode resultar em maior valor logístico.
                  </p>
                </div>
              </div>
            </div>
            <div className="brut-border bg-white text-black p-5">
              <div className="flex items-start gap-3">
                <GraduationCap size={19} className="mt-1 flex-shrink-0" />
                <div>
                  <div className="mono text-[10px] uppercase tracking-widest font-bold">Formação</div>
                  <p className="mt-2 text-[13px] leading-[1.45]">
                    Formação sobre os produtos em Lisboa, Vagos ou no próprio cliente, mediante disponibilidade de agenda. No cliente, pode haver custo de deslocamento.
                  </p>
                </div>
              </div>
            </div>
            <div className="brut-border bg-[#FFD60A] text-black p-5">
              <div className="mono text-[10px] uppercase tracking-widest font-bold">Resposta a candidaturas</div>
              <div className="display text-[36px] font-bold leading-none mt-2 flex items-center gap-2">
                {CONTACT.response}
                <ArrowRight size={26} />
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

const Label = ({ children }) => (
  <label className="mono text-[11px] uppercase tracking-widest font-bold">{children}</label>
);

const Field = ({ label, value, onChange, type = "text", required }) => (
  <div>
    <Label>{label}{required && <span className="text-[#E63946] ml-1">*</span>}</Label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full mt-2 brut-border bg-white px-4 py-3 text-[14px] outline-none focus:bg-[#FFD60A]/30 transition-colors"
    />
  </div>
);

const Select = ({ label, value, onChange, options, fullWidth }) => (
  <div className={fullWidth ? "sm:col-span-2" : ""}>
    <Label>{label}</Label>
    <div className="relative mt-2">
      <select
        value={value}
        onChange={onChange}
        className="w-full brut-border bg-white px-4 py-3 text-[14px] outline-none focus:bg-[#FFD60A]/30 transition-colors appearance-none cursor-pointer"
      >
        {options.map((option) => <option key={option}>{option}</option>)}
      </select>
      <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 mono text-[12px]">v</span>
    </div>
  </div>
);

const ContactCard = ({ icon, label, value, link, external }) => {
  const inner = (
    <div className="brut-border bg-[#F4F0E8] text-black p-5 brut-card">
      <div className="mono text-[10px] uppercase tracking-widest text-black/60 mb-1 flex items-center gap-2">
        {icon}{label}
      </div>
      <div className="text-[16px] font-bold mt-1">{value}</div>
    </div>
  );
  return link ? <a href={link} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined}>{inner}</a> : inner;
};

export default ApplyForm;
